// assets/AllCourses/Exercises/WordFormationExercise.jsx

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { StringField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function WordFormationExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResults = false,
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.wordFormationQuestions)
    ? exercise.wordFormationQuestions.map((q) => ({
        baseWord: String(q.baseWord || "").trim(),
        options: Array.isArray(q.options)
          ? q.options.map((o) => String(o).trim())
          : [],
        correctAnswers: Array.isArray(q.correctAnswers)
          ? q.correctAnswers.map((a) => String(a).trim())
          : [],
        requireCapitalization: Boolean(q.requireCapitalization),
      }))
    : [];

  /* ================= STATE ================= */
  const [answers, setAnswers] = useState({});
  const completedRef = useRef(false);

  /* ================= RESET (VERY IMPORTANT) ================= */
  useEffect(() => {
    if (!exercise?._id) return;

    setAnswers({});
    completedRef.current = false;
  }, [exercise._id]);

  useEffect(() => {
    if (!showResults) completedRef.current = false;
  }, [showResults]);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "", requireCap = false) => {
    let t = String(text || "").trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "").replace(/\s+/g, " ");
  };

  /* ================= EDIT MODE ================= */
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("wordFormationQuestions", next);
  };

  const handleBaseWordChange = (qIdx, value) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = { ...next[qIdx], baseWord: value.trim() };
    updateQuestions(next);
  };

  const handleOptionChange = (qIdx, optIdx, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    const q = next[qIdx];
    const options = [...q.options];
    options[optIdx] = value.trim();

    next[qIdx] = {
      ...q,
      options,
      correctAnswers: q.correctAnswers.filter((a) =>
        options.includes(a)
      ),
    };

    updateQuestions(next);
  };

  const handleAddOption = (qIdx) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      options: [...next[qIdx].options, ""],
    };
    updateQuestions(next);
  };

  const handleRemoveOption = (qIdx, optIdx) => {
    if (!isEditMode) return;

    const next = [...questions];
    const q = next[qIdx];
    const options = q.options.filter((_, i) => i !== optIdx);

    next[qIdx] = {
      ...q,
      options,
      correctAnswers: q.correctAnswers.filter((a) =>
        options.includes(a)
      ),
    };

    updateQuestions(next);
  };

  const handleCorrectAnswersChange = (qIdx, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    const q = next[qIdx];

    const cleaned = value
      .split(",")
      .map((v) => v.trim())
      .filter((v) => q.options.includes(v));

    next[qIdx] = { ...q, correctAnswers: cleaned };
    updateQuestions(next);
  };

  const handleCapitalizationChange = (qIdx, checked) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      requireCapitalization: Boolean(checked),
    };
    updateQuestions(next);
  };

  const handleAddQuestion = () => {
    if (!isEditMode) return;
    updateQuestions([
      ...questions,
      {
        baseWord: "",
        options: [],
        correctAnswers: [],
        requireCapitalization: false,
      },
    ]);
  };

  const handleRemoveQuestion = (qIdx) => {
    if (!isEditMode) return;
    updateQuestions(questions.filter((_, i) => i !== qIdx));
  };

  /* ================= STUDENT MODE ================= */
  const handleToggle = (qIdx, option) => {
    if (isEditMode || showResults) return;

    setAnswers((prev) => {
      const current = prev[qIdx] || [];

      const updated = current.includes(option)
        ? current.filter((o) => o !== option)
        : [...current, option];

      const newAnswers = {
        ...prev,
        [qIdx]: updated,
      };

      // 🔥 ALWAYS notify parent (IMPORTANT)
      onComplete(newAnswers, false);

      const allAnswered = questions.every(
        (_, i) =>
          Array.isArray(newAnswers[i]) && newAnswers[i].length > 0
      );

      if (allAnswered && !completedRef.current) {
        completedRef.current = true;

        const allCorrect = questions.every((q, i) => {
          const user = newAnswers[i].map((a) =>
            normalize(a, q.requireCapitalization)
          );
          const correct = q.correctAnswers.map((a) =>
            normalize(a, q.requireCapitalization)
          );

          return (
            user.length === correct.length &&
            correct.every((c) => user.includes(c))
          );
        });

        onComplete(newAnswers, allCorrect);
      }

      return newAnswers;
    });
  };

  /* ================= EDIT MODE UI ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="p-6 space-y-6">
            <StringField
              label={`Base Word ${qIdx + 1}`}
              value={q.baseWord}
              onChange={(v) => handleBaseWordChange(qIdx, v)}
              placeholder="happy"
              required
            />

            <div className="space-y-3">
              <label className="font-medium">Options</label>
              {q.options.map((opt, optIdx) => (
                <div key={optIdx} className="flex gap-3">
                  <StringField
                    value={opt}
                    onChange={(v) =>
                      handleOptionChange(qIdx, optIdx, v)
                    }
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() =>
                      handleRemoveOption(qIdx, optIdx)
                    }
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAddOption(qIdx)}
              >
                Add Option
              </Button>
            </div>

            <StringField
              label="Correct Answers"
              value={q.correctAnswers.join(", ")}
              onChange={(v) =>
                handleCorrectAnswersChange(qIdx, v)
              }
              placeholder="happiness, happily"
              required
            />

            <CapitalizationRule
              checked={q.requireCapitalization}
              onChange={(c) =>
                handleCapitalizationChange(qIdx, c)
              }
            />

            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleRemoveQuestion(qIdx)}
            >
              Remove Question
            </Button>
          </Card>
        ))}

        <Button variant="outline" onClick={handleAddQuestion}>
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>
            No questions available
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= STUDENT UI ================= */
  return (
    <Card className="max-w-3xl mx-auto">
      <CardHeader>
        <CardTitle>Word Formation</CardTitle>
        <CardDescription>
          Select correct forms of the word
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, qIdx) => {
          const selected = answers[qIdx] || [];

          return (
            <div
              key={qIdx}
              className="p-6 rounded-xl border bg-muted/40 space-y-4"
            >
              <p className="text-xl font-bold text-blue-700">
                {q.baseWord}
              </p>

              {q.options.map((opt, optIdx) => {
                const isSelected = selected.includes(opt);

                const isCorrect =
                  normalize(opt, q.requireCapitalization) ===
                  normalize(
                    q.correctAnswers.find(
                      (a) =>
                        normalize(a, q.requireCapitalization) ===
                        normalize(opt, q.requireCapitalization)
                    ),
                    q.requireCapitalization
                  );

                return (
                  <div
                    key={optIdx}
                    className={cn(
                      "flex items-center gap-3 rounded-lg border p-4",

                      isSelected && !showResults && "bg-accent",

                      showResults &&
                        isCorrect &&
                        "bg-green-100 border-green-400",

                      showResults &&
                        isSelected &&
                        !isCorrect &&
                        "bg-red-100 border-red-400"
                    )}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() =>
                        handleToggle(qIdx, opt)
                      }
                      disabled={showResults}
                    />
                    <Label className="cursor-pointer flex-1">
                      {opt}
                    </Label>
                  </div>
                );
              })}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}